if (!customElements.get('band-upsell')) {
  customElements.define(
    'band-upsell',
    class BandUpsell extends HTMLElement {
      constructor() {
        super();
        this.options = Array.from(
          this.querySelectorAll('.band-upsell__option'),
        );
        this.mainPriceCents = parseInt(this.dataset.mainPrice, 10) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        this.options.forEach((option) => {
          option.addEventListener('click', (event) => {
            event.preventDefault();
            if (
              option.classList.contains('is-sold-out') ||
              option.hasAttribute('disabled')
            )
              return;
            this.toggleOption(option);
            this.updateButtonPrice();
          });
        });
      }

      connectedCallback() {
        this.productForm = document.querySelector('product-form form');
        if (this.productForm && !this.productForm.dataset.bandUpsellBound) {
          this.productForm.dataset.bandUpsellBound = 'true';
          this.productForm.addEventListener(
            'submit',
            this.onFormSubmit.bind(this),
            true,
          );
        }
        this.captureButtonPrefix();
        this.bindQuantityListener();
        // If a device-qty-selector or custom-product-selector is on the page, refresh the button price
        // when the customer picks a different qty/discount option.
        document.addEventListener('device-qty:change', () =>
          this.updateButtonPrice(),
        );
        document.addEventListener('custom-product:change', () =>
          this.updateButtonPrice(),
        );
        this.updateButtonPrice();
      }

      captureButtonPrefix() {
        const labelEl = document.querySelector(
          '.product-form__submit [data-add-to-cart-label]',
        );
        if (labelEl) {
          this.buttonPrefix = labelEl.textContent.trim();
          this.usePriceSpan = true;
          return;
        }
        const span = document.querySelector('.product-form__submit span');
        if (!span) return;
        const text = span.textContent.trim();
        const dashIdx = text.lastIndexOf(' - ');
        this.buttonPrefix =
          dashIdx !== -1 ? text.substring(0, dashIdx).trim() : text;
        this.usePriceSpan = false;
      }

      bindQuantityListener() {
        if (!this.productForm) return;
        this.qtyInput = this.productForm.querySelector(
          'input[name="quantity"]',
        );
        if (!this.qtyInput) return;
        const handler = () => this.updateButtonPrice();
        this.qtyInput.addEventListener('input', handler);
        this.qtyInput.addEventListener('change', handler);
      }

      getQuantity() {
        const raw = this.qtyInput && this.qtyInput.value;
        const qty = parseInt(raw, 10);
        return qty > 0 ? qty : 1;
      }

      get selectedOptions() {
        return this.options.filter((opt) =>
          opt.classList.contains('is-selected'),
        );
      }

      toggleOption(option) {
        const isSelected = option.classList.contains('is-selected');
        option.setAttribute('aria-pressed', isSelected ? 'false' : 'true');
        option.classList.toggle('is-selected', !isSelected);
      }

      updateButtonPrice() {
        if (!this.buttonPrefix) return;

        const bandsCents = this.selectedOptions.reduce((sum, opt) => {
          return sum + (parseInt(opt.dataset.price, 10) || 0);
        }, 0);
        const qty = this.getQuantity();
        // If a device-qty-selector or custom-product-selector is on the page, its active option owns the
        // device subtotal: the merchant-configured bundle of slot variants
        // (with extra-discount % already baked in by Liquid). The qty stepper
        // multiplies the entire bundle. Without either, fall back to the
        // main variant price × qty.
        const deviceQty = document.querySelector('device-qty-selector');
        let deviceSubtotalCents;
        if (deviceQty && deviceQty.dataset.activeVariants) {
          deviceSubtotalCents =
            (parseInt(deviceQty.dataset.activeTotalCents, 10) || 0) * qty;
        } else {
          deviceSubtotalCents = this.mainPriceCents * qty;
        }
        const totalCents = deviceSubtotalCents + bandsCents;
        const formatted = this.formatMoney(totalCents, this.moneyFormat);

        if (this.usePriceSpan) {
          const priceEl = document.querySelector(
            '.product-form__submit [data-add-to-cart-price]',
          );
          if (priceEl) {
            priceEl.textContent = formatted;
            return;
          }
        }
        const span = document.querySelector('.product-form__submit span');
        if (span) span.textContent = `${this.buttonPrefix} - ${formatted}`;
      }

      formatMoney(cents, format) {
        const value = cents / 100;
        const formatWithDelimiters = (
          number,
          precision,
          thousands,
          decimal,
        ) => {
          const parts = number.toFixed(precision).split('.');
          const dollars = parts[0].replace(
            /(\d)(?=(\d{3})+$)/g,
            `$1${thousands}`,
          );
          const cents = parts[1] ? decimal + parts[1] : '';
          return dollars + cents;
        };

        const placeholders = {
          amount: () => formatWithDelimiters(value, 2, ',', '.'),
          amount_no_decimals: () => formatWithDelimiters(value, 0, ',', '.'),
          amount_with_comma_separator: () =>
            formatWithDelimiters(value, 2, '.', ','),
          amount_no_decimals_with_comma_separator: () =>
            formatWithDelimiters(value, 0, '.', ','),
          amount_with_apostrophe_separator: () =>
            formatWithDelimiters(value, 2, "'", '.'),
          amount_with_space_separator: () =>
            formatWithDelimiters(value, 2, ' ', ','),
          amount_no_decimals_with_space_separator: () =>
            formatWithDelimiters(value, 0, ' ', ','),
        };

        return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => {
          const fn = placeholders[key];
          return fn ? fn() : match;
        });
      }

      onFormSubmit(event) {
        const form = event.currentTarget;
        this.restoreForm(form);

        const selected = this.selectedOptions;
        const deviceQty = document.querySelector('device-qty-selector');
        const activeVariantsRaw =
          deviceQty && deviceQty.dataset.activeVariants
            ? deviceQty.dataset.activeVariants.split(',').filter(Boolean)
            : [];
        const useDeviceQty = activeVariantsRaw.length > 0;

        // Nothing to inject — let the form submit normally with main variant.
        if (selected.length === 0 && !useDeviceQty) return;

        const mainIdInput = form.querySelector('input[name="id"]');
        const mainVariantId = mainIdInput && mainIdInput.value;
        if (!mainVariantId) return;

        // Read the quantity the user picked. Defaults to 1 if the input is missing/invalid.
        const qtyInput = form.querySelector('input[name="quantity"]');
        const mainQuantity =
          qtyInput && parseInt(qtyInput.value, 10) > 0
            ? String(parseInt(qtyInput.value, 10))
            : '1';

        mainIdInput.setAttribute('data-band-upsell-original-name', 'id');
        mainIdInput.setAttribute('name', '_band_upsell_disabled_id');

        // Disable the standalone quantity field too so Shopify only sees the items[] array.
        if (qtyInput) {
          qtyInput.setAttribute('data-band-upsell-original-name', 'quantity');
          qtyInput.setAttribute('name', '_band_upsell_disabled_quantity');
        }

        let index = 0;
        selected.forEach((option) => {
          const variantId = option.dataset.variantId;
          if (!variantId) return;
          this.appendBandInput(form, `items[${index}][id]`, variantId);
          this.appendBandInput(form, `items[${index}][quantity]`, '1');
          index += 1;
        });

        if (useDeviceQty) {
          // The active option's slot variants replace the main product.
          // Handle both simple IDs (device-qty-selector) and variant:qty pairs (custom-product-selector).
          activeVariantsRaw.forEach((entry) => {
            const [id, customQty] = entry.split(':');
            const finalQty = customQty
              ? String(parseInt(customQty, 10) * parseInt(mainQuantity, 10))
              : mainQuantity;

            this.appendBandInput(form, `items[${index}][id]`, id);
            this.appendBandInput(form, `items[${index}][quantity]`, finalQty);
            index += 1;
          });
        } else {
          this.appendBandInput(form, `items[${index}][id]`, mainVariantId);
          this.appendBandInput(form, `items[${index}][quantity]`, mainQuantity);
        }
      }

      appendBandInput(form, name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-band-upsell-input', '');
        form.appendChild(input);
      }

      restoreForm(form) {
        form
          .querySelectorAll('[data-band-upsell-input]')
          .forEach((el) => el.remove());
        form
          .querySelectorAll('[data-band-upsell-original-name]')
          .forEach((el) => {
            const originalName = el.getAttribute(
              'data-band-upsell-original-name',
            );
            if (originalName) el.setAttribute('name', originalName);
            el.removeAttribute('data-band-upsell-original-name');
          });
      }
    },
  );
}
